import config from '@packages/config';
import { cached } from '@packages/shared/src/utils/cached/cached';
import { cmsFetcher } from '@packages/shared/src/utils/cmsFetcher/cmsFetcher';
import { logger } from '@packages/shared/src/utils/logger/logger';
import type { TimeControl } from '../../../../../interfaces';
import { Audiences } from '../../../../../interfaces';
import type { PromotionBannerItemType } from '../../../../../interfaces/contentSnippet';
import {
  createFilterForCurrentAudiences,
  createFilterForTimeControl,
} from '../../../../utils/filterResult';
import type { ContentSnippetApiData, Promotion } from '../../types';
import { mapDrupalContentSnippetsToPromotions } from '../mapDataToPromotion';

export const getValidPromotions = async (
  data: ContentSnippetApiData,
  platform: 'app' | 'web',
  testDate?: string,
  audience: Audiences = Audiences.anonymous,
): Promise<Array<Promotion | undefined> | undefined> => {
  const filteredPromotionBanner = data?.data?.filter(
    (contentSnippet) => contentSnippet.type === 'content_snippet_bundled--promotionbanner',
  );
  if (filteredPromotionBanner?.length === 0) {
    throw new Error('no content_snippet_bundled--promotionbanner available');
  }
  // The Audiences Enum lists the customer audiences with priority from high (lower index) to low (higher index)
  // Use it as array and cut away the audiences with higher priority (lower index) than the given target audience
  const audienceArray = Object.values(Audiences);
  const audiencesLeft = audienceArray.slice(audienceArray.indexOf(audience));
  const filterTime = createFilterForTimeControl(testDate);
  const filterAudience = createFilterForCurrentAudiences(audiencesLeft);
  const currentBannerData = filteredPromotionBanner
    ?.filter(
      (contentSnippet) =>
        contentSnippet.type === 'content_snippet_bundled--promotionbanner' &&
        // filter the ContentSnippet by time
        filterTime(contentSnippet) &&
        // filter the promotion by time
        filterTime(contentSnippet.field_paragraph as TimeControl) &&
        filterAudience(contentSnippet),
    )
    ?.filter(
      ({ field_display_app: displayApp, field_display_web: displayWeb }) =>
        (platform === 'web' && displayWeb) || (platform === 'app' && displayApp),
    ) as PromotionBannerItemType[];

  // after selecting all possible promotions - sort the current banner data array by the audience priorities
  currentBannerData?.sort(
    (a, b) =>
      audienceArray.indexOf((a.field_user_status?.name as Audiences) || Audiences.anonymous) -
      audienceArray.indexOf((b.field_user_status?.name as Audiences) || Audiences.anonymous),
  );

  return (
    (currentBannerData &&
      currentBannerData.length !== 0 &&
      mapDrupalContentSnippetsToPromotions(currentBannerData, testDate)) ||
    undefined
  );
};

const fetchAndProcessPromotions = async (
  fetchUrl: string,
  platform: 'app' | 'web',
  testDate?: string,
  audience?: Audiences,
) =>
  /* fetching result depends on fetchUrl
     - from bucket I get ContentSnippetApiData
     - from own API I get Promotion-Array
  */
  cmsFetcher<ContentSnippetApiData | Promotion[]>(fetchUrl).then((data) => {
    if (Array.isArray(data) || data === null /* null means -> not found */) {
      return data;
    }
    return getValidPromotions(data, platform, testDate, audience);
  });

/**
 * fetch a contentSnippet and return a set of promotions valid by:
  - shopId
  - validation times
  - customer session
    - login status
    - customer group (tbd)

 * @param filter object with properties for applied filters
 * @returns content snippet data
 */
export const fetchContentSnippetData = async (
  {
    shopId = '-1',
    locale = 'de',
    testDate,
    audience = Audiences.anonymous,
    platform = 'web',
  }: {
    shopId?: string;
    locale?: string;
    testDate?: string;
    audience?: Audiences;
    platform?: 'app' | 'web';
  } = {},
  callOwnApi?: boolean,
): Promise<Array<Promotion | undefined> | undefined> => {
  const language = locale.substring(0, 2) || 'de';
  // INSPIRE-3268 - if shopId === "" it is not covered by the default function parameter value - so set it to "-1"
  const finalShopId = shopId || '-1';

  const fetchUrlEmptyPromotions = callOwnApi
    ? `/api/promotions/content-snippet/empty-promotions/?locale=${language}&audience=${audience}&testDate=${
        testDate || ''
      }`
    : config.promotionBanner.apiEndpoints.bucketEmptyPromotions.replace('<language>', language);

  // return no data if an empty promotion should be rendered and user is anonymous
  if (audience === Audiences.anonymous) {
    const emptyPromotionsList = await cached(
      fetchUrlEmptyPromotions,
      () =>
        cmsFetcher(fetchUrlEmptyPromotions).catch((error) => {
          // the TypeError represents a network error while the status code >= 500 is a server error
          if (error instanceof TypeError || error?.cause?.status >= 500) {
            logger.error({ error }, 'Error: Failed to fetch promotion data');
            throw error;
          }
          // its not an issue if the empty promotions list is not available
          logger.warn({ error }, 'Failed to fetch empty promotions list');
          return undefined;
        }),
      5 * 60 * 1000,
    )();
    if (Array.isArray(emptyPromotionsList) && emptyPromotionsList.includes(finalShopId)) {
      return undefined;
    }
  }

  const fetchUrl = callOwnApi
    ? `/api/promotions/content-snippet/${finalShopId}/?locale=${language}&audience=${audience}&testDate=${
        testDate || ''
      }`
    : config.promotionBanner.apiEndpoints.bucketContentSnippet
        .replace('<language>', language)
        .replace('<identifier>', finalShopId);

  const fetchFallbackUrl = callOwnApi
    ? `/api/promotions/content-snippet/-1/?locale=${language}&audience=${audience}&testDate=${
        testDate || ''
      }`
    : config.promotionBanner.apiEndpoints.bucketContentSnippet
        .replace('<language>', language)
        .replace('<identifier>', '-1');

  // await the following promises because the result "undefined" triggers the fallback
  try {
    // better debuggable way :-P
    const res = await cached(
      [fetchUrl, platform, testDate, audience].join(''),
      () => fetchAndProcessPromotions(fetchUrl, platform, testDate, audience),
      5 * 60 * 1000,
    )();

    if (res !== null) {
      // INSPIRE-4090: if the result is null, there was a 404. Therefore we don't return and go to the fallback below
      return res;
    }
  } catch (error: any) {
    // the TypeError represents a network error while the status code >= 500 is a server error
    if (error instanceof TypeError || error?.cause?.status >= 500) {
      logger.error({ error }, 'Error: Failed to fetch promotion data');
      throw error;
    }
    // all other errors are just warnings
    logger.debug({ finalShopId, error }, 'Failed to fetch promotion data for shopId');
  }

  try {
    return await cached(
      [fetchFallbackUrl, platform, testDate, audience].join(''),
      () => fetchAndProcessPromotions(fetchFallbackUrl, platform, testDate, audience),
      5 * 60 * 1000,
    )();
  } catch (error: any) {
    // the TypeError represents a network error while the status code >= 500 is a server error
    if (error instanceof TypeError || error?.cause?.status >= 500) {
      logger.error({ error }, 'Error: Failed to fetch promotion data');
      throw error;
    }
    // all other errors are just warnings
    logger.warn({ error }, 'Temporarily failed to fetch promotion data');
    return undefined;
  }
};
